import React, { useEffect, useRef } from "react";
import { Avatar, CircularProgress } from "@mui/material";
import MaButton from "../../../../Components/UI/ma-button";
import { normalizeMediaUrl } from "../../../utils/get-profile";

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";
const MAX_BYTES = 10 * 1024 * 1024;

function validateImageFile(file, onError) {
  if (!file) return false;
  if (!file.type.startsWith("image/")) {
    onError?.("Please choose a JPG or PNG image.");
    return false;
  }
  if (file.size > MAX_BYTES) {
    onError?.("Image must be 10 MB or smaller.");
    return false;
  }
  return true;
}

export function StoreLogoUpload({
  previewUrl,
  fallbackLetter,
  disabled,
  uploading,
  onFileSelect,
}) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onFileSelect?.(file);
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-x-4">
      <div className="relative shrink-0 self-start">
        <Avatar
          src={previewUrl || undefined}
          sx={{
            width: { xs: 72, sm: 88 },
            height: { xs: 72, sm: 88 },
            bgcolor: "#2E1834",
            fontSize: "2rem",
            fontWeight: 700,
          }}
        >
          {fallbackLetter}
        </Avatar>
        {uploading ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
            <CircularProgress size={28} sx={{ color: "#fff" }} />
          </div>
        ) : null}
      </div>

      <div className="w-full sm:w-auto [&>div]:w-full sm:[&>div]:w-auto">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          disabled={disabled || uploading}
          onChange={handleChange}
        />
        <MaButton
          sx={{ borderColor: "#EAEAEA" }}
          variant="outlined"
          title="Change Photo"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
        />
        <p className="mt-1 text-center text-[12px] font-bold text-[#9D9FA4] sm:text-left">
          JPG or PNG · max 10MB
        </p>
      </div>
    </div>
  );
}

export function StoreBannerUpload({
  previewUrl,
  disabled,
  uploading,
  onFileSelect,
}) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onFileSelect?.(file);
  };

  const backgroundStyle = previewUrl
    ? {
        backgroundImage: `linear-gradient(rgba(46, 24, 52, 0.35), rgba(46, 24, 52, 0.35)), url(${previewUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  return (
    <div>
      <p className="my-0 mb-2 text-[14px] font-medium text-[#111827]">Banner</p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        disabled={disabled || uploading}
        onChange={handleChange}
      />
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        style={backgroundStyle}
        className={`relative mb-4 flex h-32 w-full flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed transition-colors sm:h-36 ${
          previewUrl
            ? "border-[#E5E7EB]"
            : "border-[#D1D5DB] bg-gradient-to-r from-[#2E1834] via-[#88479A] to-[#2E1834]"
        } ${disabled || uploading ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:border-[#9CA3AF]"}`}
      >
        {uploading ? (
          <CircularProgress size={32} sx={{ color: "#fff" }} />
        ) : (
          <>
            <p
              className={`my-0 px-4 text-center text-[13px] ${
                previewUrl ? "text-white" : "text-white/90"
              }`}
            >
              {previewUrl
                ? "Click to replace banner"
                : "Drop images here or click to browse"}
            </p>
            {!previewUrl ? (
              <span className="mt-1 text-[11px] text-white/70">
                JPG or PNG · max 10MB
              </span>
            ) : null}
          </>
        )}
      </button>
    </div>
  );
}

export function useStoreBrandingFiles({ onError } = {}) {
  const [logoFile, setLogoFile] = React.useState(null);
  const [bannerFile, setBannerFile] = React.useState(null);
  const [logoPreview, setLogoPreview] = React.useState(null);
  const [bannerPreview, setBannerPreview] = React.useState(null);

  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
      if (bannerPreview?.startsWith("blob:")) URL.revokeObjectURL(bannerPreview);
    };
  }, [logoPreview, bannerPreview]);

  const selectLogo = (file) => {
    if (!validateImageFile(file, onError)) return;
    if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const selectBanner = (file) => {
    if (!validateImageFile(file, onError)) return;
    if (bannerPreview?.startsWith("blob:")) URL.revokeObjectURL(bannerPreview);
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const clearPendingFiles = () => {
    setLogoFile(null);
    setBannerFile(null);
  };

  const syncFromStore = (store) => {
    setLogoFile(null);
    setBannerFile(null);
    setLogoPreview((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return normalizeMediaUrl(store?.storeLogo);
    });
    setBannerPreview((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return normalizeMediaUrl(store?.storeBannerUrl);
    });
  };

  return {
    logoFile,
    bannerFile,
    logoPreview,
    bannerPreview,
    selectLogo,
    selectBanner,
    clearPendingFiles,
    syncFromStore,
    hasPendingUpload: Boolean(logoFile || bannerFile),
  };
}
